import { Wand2, Flame, Lightbulb, Loader2, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";

function scoreColor(score) {
  if (score >= 75) return "#10b981";
  if (score >= 55) return "#84cc16";
  if (score >= 40) return "#f59e0b";
  return "#ef4444";
}

function verdictBadgeClass(level) {
  switch (level) {
    case "viral":
      return "border-emerald-500/40 text-emerald-400";
    case "strong":
      return "border-lime-500/40 text-lime-400";
    case "moderate":
      return "border-amber-500/40 text-amber-400";
    default:
      return "border-red-500/40 text-red-400";
  }
}

export default function ViralScoreDialog({
  open,
  onOpenChange,
  darkMode,
  viralInput,
  setViralInput,
  viralResult,
  viralLoading,
  calcViralScore,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="open-viral-score-btn">
          <Wand2 className="w-4 h-4" />
          Predict Viral Score
        </Button>
      </DialogTrigger>
      <DialogContent className={darkMode ? "bg-[#0d1117] border-[#30363d] text-white" : ""}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-500" />
            Predictive Viral Score
          </DialogTitle>
          <DialogDescription className={darkMode ? "text-slate-400" : ""}>
            Paste any draft content to predict its viral potential before posting.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="viral-input">Content draft</Label>
            <Textarea
              id="viral-input"
              value={viralInput}
              onChange={(e) => setViralInput(e.target.value)}
              placeholder="Why nobody tells you the truth about Dubai company formation..."
              rows={5}
              className={darkMode ? "bg-[#161b22] border-[#30363d] text-white mt-1" : "mt-1"}
              data-testid="viral-content-input"
            />
          </div>
          {viralResult && (
            <div
              className={`p-4 rounded-lg ${darkMode ? "bg-[#161b22]" : "bg-slate-50"} space-y-3`}
              data-testid="viral-result-card"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p
                    className={`text-xs uppercase tracking-wider ${
                      darkMode ? "text-slate-400" : "text-slate-500"
                    }`}
                  >
                    Viral Score
                  </p>
                  <p className="text-4xl font-bold" style={{ color: scoreColor(viralResult.viral_score) }}>
                    {viralResult.viral_score}
                  </p>
                </div>
                <Badge
                  variant="outline"
                  className={`text-base px-3 py-1 ${verdictBadgeClass(viralResult.verdict_level)}`}
                >
                  {viralResult.verdict}
                </Badge>
              </div>
              <div className="space-y-2">
                {(viralResult.factors || []).map((f, i) => (
                  <div key={`${f.factor}-${i}`} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span>{f.factor}</span>
                      <span className={darkMode ? "text-slate-400" : "text-slate-500"}>
                        {f.score}/{f.max}
                      </span>
                    </div>
                    <div
                      className={`h-1.5 rounded-full ${
                        darkMode ? "bg-[#0d1117]" : "bg-slate-200"
                      } overflow-hidden`}
                    >
                      <div
                        className="h-full bg-gradient-to-r from-orange-500 to-pink-500"
                        style={{ width: `${(f.score / f.max) * 100}%` }}
                      />
                    </div>
                    <p className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-400"}`}>
                      {f.details}
                    </p>
                  </div>
                ))}
              </div>
              {viralResult.improvement_suggestions?.length > 0 && (
                <div
                  className={`pt-3 border-t ${
                    darkMode ? "border-[#30363d]" : "border-slate-200"
                  }`}
                >
                  <p className="text-xs font-semibold mb-2 flex items-center gap-1">
                    <Lightbulb className="w-3 h-3" /> Suggestions
                  </p>
                  <ul className="text-xs space-y-1">
                    {viralResult.improvement_suggestions.map((s, i) => (
                      <li
                        key={`sugg-${i}-${s.slice(0, 20)}`}
                        className={darkMode ? "text-slate-300" : "text-slate-700"}
                      >
                        • {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={calcViralScore} disabled={viralLoading} data-testid="calc-viral-btn">
            {viralLoading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            Calculate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
